import React from "react";
import { observer } from "mobx-react";
import { Button, Checkbox, HStack, Link, Stack, Text } from "@chakra-ui/react";
import { useStore } from "@/store";
import { translations } from "@/constants/translations";
import { renderInnerHtml } from "@/helpers/dom";
import { ScrollToElement } from "@/components/ScrollToElement";
import type { OrderCheckboxData } from "@/store/types/order";
import { useAppNavigate } from "@/routing/useAppNavigate";
import { OrderUnitDetails } from "./UnitDetails";
import { H3 } from "@/components/Heading";
import { ExtraUnitsCallout } from "@/components/ExtraUnitsCallout";

export const OrderDetailsEmergentPanel = observer(() => {
  const {
    activeLanguage,
    setOrderData,
    isValidOrderMinimum,
    orderData,
    orderCheckboxData,
    toggleOrderCheckbox,
    isEmergentFlow,
    isIndigenousFlow,
    ga4Store,
    telemetryStore,
  } = useStore();
  const language = translations.pages["order-details"][activeLanguage];
  const navigate = useAppNavigate();

  const handleCheckboxChange = (key: keyof OrderCheckboxData) =>
    toggleOrderCheckbox(key);

  const handleCheckboxEmailUpdates = (value: boolean) => {
    setOrderData({
      ...orderData,
      subscribeToEmailUpdates: value,
    });
  };

  const handleProceedNext = () => {
    if (!isValid) {
      telemetryStore.detailsContinueBlocked([
        "agreeToPrivacyPolicy",
        "confirmAge",
      ]);
      return;
    }

    telemetryStore.detailsSubmitted();
    ga4Store.addToCart();
    navigate({ to: "/order/shipping" });
  };

  const hasExtraUnitCostsCallout =
    !isIndigenousFlow && location.pathname.includes("details");

  const isValid =
    (!isEmergentFlow
      ? orderCheckboxData.agreeToTerms
      : !orderCheckboxData.agreeToTerms) &&
    orderCheckboxData.agreeToPrivacyPolicy &&
    orderCheckboxData.confirmAge &&
    isValidOrderMinimum;

  return (
    <Stack spacing="14">
      <ScrollToElement />

      {hasExtraUnitCostsCallout && <ExtraUnitsCallout />}

      <Stack spacing="8">
        <H3>{language.enter_order_details}</H3>
        <OrderUnitDetails />
      </Stack>

      <Stack spacing="4">
        <Text fontWeight="bold">{language.required}</Text>
        <Stack>
          <Checkbox
            id="checkbox1"
            isChecked={orderCheckboxData.agreeToPrivacyPolicy}
            onChange={() => handleCheckboxChange("agreeToPrivacyPolicy")}
          >
            {renderInnerHtml(language.i_agree)}
            <Link
              textDecoration="underline"
              href="https://www.emergentbiosolutions.com/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {renderInnerHtml(language.privacy_policy)}
            </Link>
            .
          </Checkbox>

          <Checkbox
            id="checkbox3"
            isChecked={orderCheckboxData.confirmAge}
            onChange={() => handleCheckboxChange("confirmAge")}
          >
            {renderInnerHtml(language.i_confirm)}
          </Checkbox>
        </Stack>
      </Stack>

      <Stack spacing="4">
        <Text fontWeight="bold">{language.optional}</Text>
        <Checkbox
          id="checkbox4"
          isChecked={orderData.subscribeToEmailUpdates}
          onChange={() =>
            handleCheckboxEmailUpdates(!orderData.subscribeToEmailUpdates)
          }
        >
          {renderInnerHtml(language.i_like_emails)}
        </Checkbox>
      </Stack>

      {!isIndigenousFlow && <Text>{renderInnerHtml(language.order_note)}</Text>}

      <HStack justifyContent="flex-end">
        <Button
          onClick={handleProceedNext}
          aria-disabled={!isValid}
        >
          {language.proceed_to_billing}
        </Button>
      </HStack>
    </Stack>
  );
});
